import type {
  IUser,
  TopAsset,
  TopAssetType,
  TopAssetsSort,
} from '@carboncopyco/types';
import { removeUndefinedValues } from '@carboncopyco/utils';
import { mainApi } from './mainApi';

export type GetTopAssetsArgs = {
  organizationId: string;
  sort?: TopAssetsSort;
  limit?: number;
  timezone?: string;
  startTime: string;
  endTime: string;
  assetTypes?: TopAssetType[];
  myAssets: boolean;
  excludeFavorites: boolean;
};

export type GetFavoritesArgs = {
  organizationId: string;
  timezone?: string;
  startTime: string;
  endTime: string;
  assetTypes?: TopAssetType[];
};

type AddToFavoritesArgs = {
  organizationId: string;
  workspaceId: string;
  assetType: TopAssetType;
  assetId: string;
  getTopAssetsArgs: GetTopAssetsArgs;
  getFavoritesArgs: GetFavoritesArgs;
};

type RemoveFromFavoritesArgs = {
  organizationId: string;
  workspaceId: string;
  assetType: TopAssetType;
  assetId: string;
  getTopAssetsArgs: GetTopAssetsArgs;
  getFavoritesArgs: GetFavoritesArgs;
};

type GetTopAssetsResponse = { items: TopAsset[] };

export type FavoriteItem = {
  id: number;
  assetId: string;
  assetType: string;
  creator: IUser;
  workspaceId: string;
  updateTime: string;
  createTime: string;
  archiveTime?: string;
  isDeleted: boolean;
};

type GetFavoritesResponse = { items: TopAsset[] };

const toggleIsFavoritePatch = (
  getAssetsResponse: GetFavoritesResponse | GetTopAssetsResponse,
  assetId: string
) => {
  const assetToChange = getAssetsResponse.items.find(
    (asset) => asset.assetId === assetId
  );
  if (assetToChange) {
    assetToChange.isFavorite = !assetToChange.isFavorite;
  }
};

const updateAddFavoriteAssetOptimistically = async (
  { getFavoritesArgs, getTopAssetsArgs, assetId },
  { dispatch, queryFulfilled }
) => {
  const patchFavoriteAssetsResults = dispatch(
    topAssetsApi.util.updateQueryData(
      'getFavorites',
      getFavoritesArgs,
      (response) => toggleIsFavoritePatch(response, assetId)
    )
  );
  const patchTopAssetsResults = dispatch(
    topAssetsApi.util.updateQueryData(
      'getTopAssets',
      getTopAssetsArgs,
      (response) => toggleIsFavoritePatch(response, assetId)
    )
  );
  try {
    await queryFulfilled;
  } catch {
    patchFavoriteAssetsResults.undo();
    patchTopAssetsResults.undo();
  }
  return;
};

const topAssetsApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    getTopAssets: builder.query<GetTopAssetsResponse, GetTopAssetsArgs>({
      query: ({
        organizationId,
        sort = 'last_updated',
        limit = 50,
        ...rest
      }) => ({
        method: 'GET',
        url: `/api/${organizationId}/assets/top-assets`,
        params: {
          sort,
          limit,
          ...rest,
          includeShared: true,
        },
      }),
      providesTags: ['TopAssets'],
    }),
    getFavorites: builder.query<GetFavoritesResponse, GetFavoritesArgs>({
      query: ({ organizationId, ...rest }) => ({
        method: 'GET',
        url: `/api/${organizationId}/assets/favorites`,
        params: removeUndefinedValues(rest),
      }),
      providesTags: ['FavoriteAssets'],
      transformResponse: (response: GetFavoritesResponse) => {
        return {
          ...response,
          items: response.items.map((item) => ({
            ...item,
            isFavorite: true,
          })),
        };
      },
    }),
    addToFavorites: builder.mutation<FavoriteItem, AddToFavoritesArgs>({
      query: ({ organizationId, assetType, assetId, workspaceId }) => ({
        method: 'PUT',
        url: `/api/${organizationId}/favorite-asset/${assetType}/${assetId}?workspace_id=${workspaceId}`,
      }),
      onQueryStarted: updateAddFavoriteAssetOptimistically,
    }),
    removeFromFavorites: builder.mutation<
      FavoriteItem,
      RemoveFromFavoritesArgs
    >({
      query: ({ organizationId, assetType, assetId, workspaceId }) => ({
        method: 'DELETE',
        url: `/api/${organizationId}/favorite-asset/${assetType}/${assetId}?workspace_id=${workspaceId}`,
      }),
      onQueryStarted: updateAddFavoriteAssetOptimistically,
    }),
    resetTopAssets: builder.mutation<null, void>({
      queryFn: () => {
        return { data: null };
      },
      invalidatesTags: ['TopAssets', 'FavoriteAssets'],
    }),
  }),
});

export const {
  useGetTopAssetsQuery,
  useGetFavoritesQuery,
  useAddToFavoritesMutation,
  useRemoveFromFavoritesMutation,
  useResetTopAssetsMutation,
} = topAssetsApi;
